import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Clé mois courant — reset automatique chaque mois (nouveau localStorage)
const now = new Date();
const MONTH_KEY = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
const KEY_PREFIX = `psygestion_sim_uses_${MONTH_KEY}`;
const MAX_USES = 3;

// Colonnes DB valides pour chaque featureKey
const DB_COLUMN: Record<string, "simulateur_uses" | "projection_uses"> = {
  simulateur: "simulateur_uses",
  projection:  "projection_uses",
};

export function useSimulationLimit(featureKey: string) {
  const localKey = `${KEY_PREFIX}_${featureKey}`;
  const dbCol = DB_COLUMN[featureKey];

  // Init depuis localStorage (synchrone → pas de flash)
  const [count, setCount] = useState(() => Number(localStorage.getItem(localKey) || "0"));

  // Au montage : récupérer le vrai compteur depuis Supabase et prendre le MAX
  // (empêche le bypass par effacement du localStorage)
  useEffect(() => {
    if (!dbCol) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase
        .from("profils")
        .select(dbCol)
        .eq("user_id", user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (!data) return;
          const serverCount = (data as Record<string, number>)[dbCol] ?? 0;
          // Source of truth = max(local, server) — évite régression si désynchro
          const trueCount = Math.max(serverCount, Number(localStorage.getItem(localKey) || "0"));
          if (trueCount !== Number(localStorage.getItem(localKey) || "0")) {
            localStorage.setItem(localKey, String(trueCount));
          }
          setCount(trueCount);
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbCol, localKey]);

  const increment = useCallback(() => {
    const next = count + 1;
    // 1. Mise à jour locale immédiate (UX fluide)
    localStorage.setItem(localKey, String(next));
    setCount(next);

    // 2. Persistance DB (source of truth, anti-bypass)
    if (dbCol) {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (!user) return;
        supabase
          .from("profils")
          .update({ [dbCol]: next } as Record<string, number>)
          .eq("user_id", user.id)
          .then(({ error }) => {
            if (error) console.error("useSimulationLimit DB sync error:", error.message);
          });
      });
    }
  }, [count, localKey, dbCol]);

  return {
    count,
    limitReached: count >= MAX_USES,
    remaining: Math.max(0, MAX_USES - count),
    increment,
  };
}
